import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import ReactDOM from "react-dom";

// Глобальная переменная для отслеживания активного tooltip
let activeTooltipId = null;
let activeTooltipSetVisible = null;
let showTimeoutId = null;

// Генератор уникальных ID
let tooltipIdCounter = 0;

// CSS для скрытия скроллбара
const scrollStyles = `
  .history-scroll::-webkit-scrollbar {
    width: 4px;
  }
  .history-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .history-scroll::-webkit-scrollbar-thumb {
    background: #D1D5DB;
    border-radius: 4px;
  }
  .history-scroll::-webkit-scrollbar-thumb:hover {
    background: #9CA3AF;
  }
`;

// Инжектим стили один раз
if (typeof document !== 'undefined' && !document.getElementById('history-tooltip-styles')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'history-tooltip-styles';
    styleSheet.textContent = scrollStyles;
    document.head.appendChild(styleSheet);
}

/**
 * Компонент для отображения истории изменений ячейки.
 */
const HistoryTooltip = memo(({ history, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [hideTimeoutId, setHideTimeoutId] = useState(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const tooltipId = useRef(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        tooltipId.current = ++tooltipIdCounter;
    }, []);

    // Обновляем позицию при показе
    const updatePosition = useCallback((element) => {
        if (element) {
            const rect = element.getBoundingClientRect();
            setPosition({
                top: rect.bottom,
                left: rect.right - 20,
            });
        }
    }, []);

    // Предотвращаем скролл страницы
    useEffect(() => {
        const scrollElement = scrollRef.current;
        if (!scrollElement || !isVisible) return;

        const handleWheel = (e) => {
            const { scrollTop, scrollHeight, clientHeight } = scrollElement;
            const isScrollable = scrollHeight > clientHeight;

            if (!isScrollable) {
                e.preventDefault();
                return;
            }

            const isAtTop = scrollTop === 0;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

            if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
                e.preventDefault();
            }
        };

        scrollElement.addEventListener('wheel', handleWheel, { passive: false });
        return () => scrollElement.removeEventListener('wheel', handleWheel);
    }, [isVisible]);

    // Если нет истории, просто возвращаем children без обёртки
    if (!history || history.length === 0) {
        return children;
    }

    const handleMouseEnter = (e) => {
        // Ищем родительский элемент с position: relative (TextCell)
        let target = e.target;
        while (target && target.parentElement) {
            const style = window.getComputedStyle(target);
            if (style.position === 'relative') {
                break;
            }
            target = target.parentElement;
        }

        if (hideTimeoutId) {
            clearTimeout(hideTimeoutId);
            setHideTimeoutId(null);
        }

        if (activeTooltipId === tooltipId.current) {
            updatePosition(target);
            setIsVisible(true);
            return;
        }

        if (showTimeoutId) {
            clearTimeout(showTimeoutId);
        }

        showTimeoutId = setTimeout(() => {
            if (activeTooltipId !== null && activeTooltipSetVisible) {
                activeTooltipSetVisible(false);
            }
            activeTooltipId = tooltipId.current;
            activeTooltipSetVisible = setIsVisible;
            updatePosition(target);
            setIsVisible(true);
        }, 400);
    };

    const handleMouseLeave = () => {
        if (showTimeoutId) {
            clearTimeout(showTimeoutId);
            showTimeoutId = null;
        }

        const id = setTimeout(() => {
            if (activeTooltipId === tooltipId.current) {
                activeTooltipId = null;
                activeTooltipSetVisible = null;
            }
            setIsVisible(false);
        }, 300);
        setHideTimeoutId(id);
    };

    const handleTooltipMouseEnter = () => {
        if (hideTimeoutId) {
            clearTimeout(hideTimeoutId);
            setHideTimeoutId(null);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString("ru-RU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatValue = (value) => {
        if (value === null || value === undefined) return "—";
        const num = Number(value);
        if (isNaN(num)) return value;
        return num.toLocaleString("ru-RU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    // Рендерим tooltip через portal
    const tooltipContent = isVisible ? ReactDOM.createPortal(
        <div
            style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                transform: "translateX(-50%)",
                zIndex: 1100,
                minWidth: "320px",
                maxWidth: "380px",
            }}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {/* Стрелка вверх */}
            <div
                style={{
                    position: "absolute",
                    top: "-6px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "8px solid transparent",
                    borderRight: "8px solid transparent",
                    borderBottom: "8px solid #54606F",
                    zIndex: 1,
                }}
            />

            {/* Popup контейнер */}
            <div
                style={{
                    background: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
                    border: "1px solid #E1E6F1",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        backgroundColor: "#54606F",
                        padding: "10px 14px",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                    }}
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                    </svg>
                    История изменений
                </div>

                {/* Список изменений */}
                <div
                    ref={scrollRef}
                    className="history-scroll"
                    style={{
                        maxHeight: "220px",
                        overflowY: "auto",
                        overscrollBehavior: "contain",
                    }}
                >
                    {history.map((item, index) => (
                        <div
                            key={index}
                            style={{
                                padding: "12px 14px",
                                borderBottom: index < history.length - 1 ? "1px solid #E1E6F1" : "none",
                                backgroundColor: index % 2 === 0 ? "#fff" : "#F8F9FB",
                            }}
                        >
                            {/* Дата и имя пользователя */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: "10px",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#8494AA"
                                        strokeWidth="2"
                                    >
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                    <span style={{ fontSize: "11px", color: "#8494AA" }}>
                                        {formatDate(item.changedAt)}
                                    </span>
                                </div>

                                <span style={{ fontSize: "12px", color: "#54606F", fontWeight: 500 }}>
                                    {item.userFullName || "Неизвестный"}
                                </span>
                            </div>

                            {/* Значения: старое → новое */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "6px 10px",
                                    background: "#E8EBEF",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                }}
                            >
                                <span
                                    style={{
                                        flex: 1,
                                        color: "#D32F2F",
                                        fontWeight: 500,
                                        fontFamily: "monospace",
                                        textAlign: "left",
                                    }}
                                >
                                    {formatValue(item.previousValue)}
                                </span>
                                <span style={{
                                    color: "#8494AA",
                                    padding: "0 12px",
                                    flexShrink: 0,
                                }}>→</span>
                                <span
                                    style={{
                                        flex: 1,
                                        color: "#388E3C",
                                        fontWeight: 500,
                                        fontFamily: "monospace",
                                        textAlign: "right",
                                    }}
                                >
                                    {formatValue(item.newValue)}
                                </span>
                            </div>

                            {/* Комментарий */}
                            {item.comment && (
                                <div
                                    style={{
                                        marginTop: "8px",
                                        padding: "6px 10px",
                                        background: "#FFF3E0",
                                        borderRadius: "4px",
                                        fontSize: "11px",
                                        color: "#E65100",
                                        borderLeft: "2px solid #FF9800",
                                    }}
                                >
                                    {item.comment}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    ) : null;

    // Клонируем children с добавлением событий hover
    return (
        <>
            {React.cloneElement(children, {
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
            })}
            {tooltipContent}
        </>
    );
});

HistoryTooltip.displayName = "HistoryTooltip";

export default HistoryTooltip;
